import { themeAtom } from "../../shared/atoms";
import { RouteParams } from "../models/VoyagePnl.model";
import { continueClickedAtomFamily, formatNumberWithDots, PnlContext } from "../utils/UtilFunctions";
import VarianceHelpPanel from "./VarianceHelpPanel";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const VarianceSection: React.FC = () => {
  const theme = useAtomValue(themeAtom);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const { tcCode, voyageNo } = useParams<RouteParams>();
  const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);
  const continueClicked = useAtomValue(continueClickedAtomFamily(voyageScope));

  const voyagePnlData = useAtomValue(PnlContext);
  if (!voyagePnlData) return null; // or a loading state, fallback UI, etc.

  return (
    <>
      <div className="mb-6 mt-4 flex w-full flex-row items-center justify-between gap-10">
        <div
          className={twMerge(
            "mx-auto w-[25%] space-y-2 rounded-lg border p-6",
            theme.uwhcAssistant.tableBg,
            theme.uwhcAssistant.border,
          )}
        >
          <div className="font-medium">Estimate:</div>
          <div className="text-[24px] font-bold">{formatNumberWithDots(voyagePnlData.pnlParams.estimate)}</div>
        </div>
        <div
          className={twMerge(
            "mx-auto w-[25%] space-y-2 rounded-lg border p-6",
            theme.uwhcAssistant.tableBg,
            theme.uwhcAssistant.border,
          )}
        >
          <div className="font-medium">Actual:</div>
          <div className="text-[24px] font-bold">{formatNumberWithDots(voyagePnlData.pnlParams.actual)}</div>
        </div>

        <div
          className={twMerge(
            "mx-auto w-[25%] space-y-2 rounded-lg border p-6",
            theme.uwhcAssistant.tableBg,
            theme.uwhcAssistant.border,
          )}
        >
          <div className="font-medium">Variance:</div>

          <div
            className={`text-[24px] font-bold ${voyagePnlData.pnlParams.variance < 0 ? "text-[#AF0000]" : "text-[#1F8475]"}`}
          >
            {formatNumberWithDots(voyagePnlData.pnlParams.variance)}
          </div>
        </div>

        <div
          className={twMerge(
            "mx-auto w-[25%] space-y-2 rounded-lg border p-6",
            theme.uwhcAssistant.tableBg,
            theme.uwhcAssistant.border,
          )}
        >
          <div className="font-medium">Unallocated:</div>
          <div className="text-[24px] font-bold">{formatNumberWithDots(voyagePnlData.pnlParams.unallocated)}</div>
        </div>
      </div>

      <div className="mb-8 w-full">
        <div className="mb-4 flex w-full items-center justify-between gap-3">
          <div className="text-[18px] font-bold">
            {continueClicked ? "Review before submitting" : "Variance breakdown"}
          </div>
        </div>
        <div className="flex w-full items-start justify-between gap-4">
          <div className={twMerge("max-w-[512px] text-[14px]", theme.uwhcAssistant.text)}>
            Select the key drivers that contributed to the variance. For each key driver, specify the variance amount
            and the impacted category. The system will automatically calculate the impact.
          </div>

          <div className="shrink-0">
            <button
              type="button"
              onClick={() => setIsSidePanelOpen(true)}
              className={twMerge(
                "ml-10 flex shrink-0 items-center gap-[8px] rounded border border-green px-4 py-2 text-[14px] text-green hover:bg-green focus:outline-none",
                theme.headerComponent.notifications.itemBackground,
                theme.headerComponent.tailwind.buttonHoverText,
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <path d="M12 17h.01"></path>
              </svg>
              <span>Help</span>
            </button>
          </div>
        </div>
      </div>

      <VarianceHelpPanel isOpen={isSidePanelOpen} onClose={() => setIsSidePanelOpen(false)} />
    </>
  );
};

export default VarianceSection;
